import { orgAPI } from "@/api/api";
import { useCopyFileRecord } from "@/api/fileRecords";
import { useWorkspaces } from "@/api/workspaces";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { FileRecord } from "@cohesive/api-client";
import { Layers, User } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

interface CopyToWorkspaceDialogProps {
  /** The file record to copy (typically a canvas-class file). */
  record: FileRecord | null;
  onClose: () => void;
  /** Called when the copy succeeds, before `onClose`. */
  onSuccess?: () => void;
}

function getLeafName(path: string | undefined | null): string {
  // Canvas entities can be minimal (id/title only, no path) — guard so the
  // dialog never throws on an absent path.
  if (!path) return "";
  const cleaned = path.endsWith("/") ? path.slice(0, -1) : path;
  const segments = cleaned.split("/").filter(Boolean);
  return segments[segments.length - 1] || cleaned;
}

function splitLeaf(leaf: string): { base: string; ext: string } {
  const dot = leaf.lastIndexOf(".");
  // Treat a leading dot (e.g. ".env") as part of the base, not an extension.
  if (dot <= 0) return { base: leaf, ext: "" };
  return { base: leaf.slice(0, dot), ext: leaf.slice(dot) };
}

/** Root-level entry names in a workspace — what a root copy can collide with. */
function rootLeafNames(records: FileRecord[]): Set<string> {
  const names = new Set<string>();
  for (const r of records) {
    if (!r.path) continue;
    const cleaned = r.path.endsWith("/") ? r.path.slice(0, -1) : r.path;
    const segments = cleaned.split("/").filter(Boolean);
    if (segments.length === 1) names.add(segments[0]);
  }
  return names;
}

/** Pick a non-colliding leaf, suffixing " (n)" before the extension. */
function disambiguateLeaf(leaf: string, taken: Set<string>): string {
  if (!taken.has(leaf)) return leaf;
  const { base, ext } = splitLeaf(leaf);
  let n = 1;
  let candidate = `${base} (${n})${ext}`;
  while (taken.has(candidate)) {
    n += 1;
    candidate = `${base} (${n})${ext}`;
  }
  return candidate;
}

/**
 * Copies a file record into a chosen workspace (non-destructive — the source,
 * e.g. a canvas file, is untouched). Mirrors MoveFileRecordDialog's picker UI
 * but runs a copy rather than a move. The backend 409s on an exact path
 * collision, so we pre-resolve a unique root leaf in the destination first.
 */
export function CopyToWorkspaceDialog({
  record,
  onClose,
  onSuccess,
}: CopyToWorkspaceDialogProps) {
  const [targetId, setTargetId] = useState<string>("");
  const [isCopying, setIsCopying] = useState(false);
  const { data: workspaces = [] } = useWorkspaces();
  const copyFileRecord = useCopyFileRecord(undefined);

  // Reset when a new record is opened.
  useEffect(() => {
    if (record) {
      setTargetId("");
      setIsCopying(false);
    }
  }, [record?.id]);

  // Team workspaces first, private last, alpha within each group.
  const sortedWorkspaces = [...workspaces].sort((a, b) => {
    if (a.is_private === b.is_private) return a.name.localeCompare(b.name);
    return a.is_private ? 1 : -1;
  });

  const handleCopy = async () => {
    if (!record || !targetId || isCopying) return;

    setIsCopying(true);
    try {
      // The record handed in (e.g. a canvas entity) can be a minimal shape
      // without `path`; fetch the authoritative record so the copy lands under
      // its real filename.
      const sourcePath =
        record.path || (await orgAPI.fileRecords.getById(record.id)).path;
      const leaf = getLeafName(sourcePath) || "file";

      // Resolve a unique name at the destination root so the copy doesn't
      // 409 on an existing file with the same name.
      const destRecords = await orgAPI.fileRecords.list({
        class: "workspace",
        workspace_id: targetId,
      });
      const path = disambiguateLeaf(leaf, rootLeafNames(destRecords));

      await copyFileRecord.mutateAsync({
        fileRecordId: record.id,
        payload: {
          destination: { class: "workspace", workspace_id: targetId, path },
        },
      });
      toast.success("Copied to workspace");
      onSuccess?.();
      onClose();
    } catch {
      toast.error("Failed to copy file");
    } finally {
      setIsCopying(false);
    }
  };

  return (
    <Dialog
      open={record !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Copy to workspace</DialogTitle>
          <DialogDescription>
            Choose a workspace to copy
            {getLeafName(record?.path)
              ? ` "${getLeafName(record?.path)}"`
              : " this file"}{" "}
            into.{" "}
            {record?.class === "flow-session"
              ? "The original stays in this chat."
              : "The original stays on the canvas."}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <Select value={targetId} onValueChange={setTargetId}>
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Select a workspace…" />
            </SelectTrigger>
            <SelectContent>
              {sortedWorkspaces.length === 0 ? (
                <div className="px-3 py-6 text-sm text-muted-foreground text-center">
                  No workspaces available.
                </div>
              ) : (
                sortedWorkspaces.map((ws) => {
                  const Icon = ws.is_private ? User : Layers;
                  const label = ws.is_private ? "My Workspace" : ws.name;
                  return (
                    <SelectItem key={ws.id} value={ws.id} className="py-2.5">
                      <span className="flex items-center gap-2.5">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-foreground/[0.06] dark:bg-n-95/[0.06]">
                          <Icon className="h-3.5 w-3.5 text-foreground/60" />
                        </span>
                        <span className="text-[13px] font-medium truncate">
                          {label}
                        </span>
                      </span>
                    </SelectItem>
                  );
                })
              )}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleCopy} disabled={!targetId || isCopying}>
            {isCopying ? "Copying…" : "Copy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
